
import React, { useContext, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Plus, Users, Grid3X3, List, Workflow, Columns3Icon, ChevronLeft, ChevronRight } from "lucide-react";
import { ProjectContext } from "./ProjectStructureTab";
import { useTaskManagement } from './task-management/hooks/useTaskManagement';
import { useResourceAssignmentOptimized } from './resource-assignment/hooks/useResourceAssignmentOptimized';
import TasksList from './resource-assignment/TasksList';
import SubtasksList from './task-management/components/SubtasksList';
import SubtasksSummaryView from './task-management/components/SubtasksSummaryView';
import StepsContainer from './task-management/components/StepsContainer';
import SubtaskForm from './task-management/components/SubtaskForm';
import ProjectInfoHeader from './resource-assignment/ProjectInfoHeader';
import DeleteConfirmationDialog from './task-management/components/DeleteConfirmationDialog';
import SuccessDialog from './task-management/components/SuccessDialog';
import { useUserRole } from './hooks/useUserRole';
import { useTaskManagementSubtaskAccess } from './hooks/useTaskManagementSubtaskAccess';
import { useManagerHierarchyEmails } from './hooks/useManagerHierarchyEmails';
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";


interface TaskManagementTabProps {
  user: User | null;
}

const TaskManagementTab = ({ user }: TaskManagementTabProps) => {
  const { selectedProject } = useContext(ProjectContext);
  const { role } = useUserRole(user);
  const { canManagerAddSubtasks } = useTaskManagementSubtaskAccess(user);
  const { allowedEmails: managerHierarchyEmails } = useManagerHierarchyEmails(
    user,
    role === 'Manager' && canManagerAddSubtasks
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSubtask, setEditingSubtask] = useState(null);
  const [viewMode, setViewMode] = useState('steps'); // 'cards', 'summary', or 'steps'
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subtaskToDelete, setSubtaskToDelete] = useState<string | null>(null);
  const [stepToDelete, setStepToDelete] = useState<number | null>(null);
  const [deleteType, setDeleteType] = useState<'subtask' | 'step' | null>(null);
  
  // Success dialog state
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: '', description: '' });
  
  // Collapse state for Project Tasks section
  const [isTasksListCollapsed, setIsTasksListCollapsed] = useState(false);
  
  const {
    subtasks,
    selectedTaskId,
    setSelectedTaskId,
    isLoading,
    projectTasks,
    uniqueTaskIds,
    saveSubtask,
    updateSubtask,
    deleteSubtask,
    updateSubtaskStatus,
    moveSubtask,
    addStep,
    editStep,
    deleteStep,
    getExistingSteps,
    availableSteps
  } = useTaskManagement(selectedProject, { useElevatedWrites: canManagerAddSubtasks || role === 'Director' });

  // Check if user owns any project_subtasks records in this project OR is the project owner
  const isProjectSubtaskOwner = user ? subtasks.some(subtask => subtask.user_id === user.id) : false;
  
  // Check if the current user is the project owner by checking if they created the project
  // We use a simple database lookup since the Project interface doesn't include user_id
  const [isProjectOwner, setIsProjectOwner] = useState(false);
  
  useEffect(() => {
    const checkProjectOwnership = async () => {
      if (!user || !selectedProject) {
        setIsProjectOwner(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('projects')
          .select('user_id')
          .eq('id', selectedProject.id)
          .eq('user_id', user.id)
          .single();

        setIsProjectOwner(!error && data !== null);
      } catch (err) {
        console.error('Error checking project ownership:', err);
        setIsProjectOwner(false);
      }
    };

    checkProjectOwnership();
  }, [user, selectedProject]);
  
  // For Project Managers, the Add Subtask / Add New Step / edit / delete / move
  // permissions are controlled exclusively by tm_addsubtaskandstep__project_manager.
  // Owning previously-created subtasks must NOT bypass this flag.
  const isRestrictedUser = role === 'Manager'
    ? !canManagerAddSubtasks
    : (!isProjectSubtaskOwner && !isProjectOwner && !canManagerAddSubtasks);

  const { 
    assignments, 
    resources, 
    projectTasks: resourceProjectTasks,
    selectedTaskId: resourceSelectedTaskId,
    setSelectedTaskId: resourceSetSelectedTaskId,
    uniqueTaskIds: resourceUniqueTaskIds,
    isLoading: resourceIsLoading,
    fetchTaskAssignments 
  } = useResourceAssignmentOptimized(selectedProject);

  const getAssignmentsForTask = () => {
    return assignments;
  };

  const getSelectedTaskDetails = () => {
    if (!selectedTaskId || !projectTasks) return null;
    return projectTasks.find(task => (task.taskId || task.name) === selectedTaskId);
  };

  const currentTaskAssignments = selectedTaskId ? getAssignmentsForTask() : [];
  const sortedSubtasks = [...subtasks].sort((a, b) => 
    a.subtask_name.localeCompare(b.subtask_name)
  );

  const handleAddSubtask = () => {
    if (!selectedTaskId || (currentTaskAssignments.length === 0 && subtasks.length === 0)) {
      return;
    }
    setEditingSubtask(null);
    setIsDialogOpen(true);
  };

  const handleEditSubtask = (subtask) => {
    setEditingSubtask(subtask);
    setIsDialogOpen(true);
  };

  const handleDeleteSubtask = (subtaskId: string) => {
    setSubtaskToDelete(subtaskId);
    setDeleteType('subtask');
    setDeleteDialogOpen(true);
  };

  const handleDeleteStep = (step: number) => {
    setStepToDelete(step);
    setDeleteType('step');
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteType === 'subtask' && subtaskToDelete) {
        await deleteSubtask(subtaskToDelete);
        setSuccessMessage({
          title: 'Subtask Deleted',
          description: 'The subtask has been successfully deleted.'
        });
        setSuccessDialogOpen(true);
      } else if (deleteType === 'step' && stepToDelete !== null) {
        await deleteStep(stepToDelete);
        setSuccessMessage({
          title: 'Step Deleted',
          description: 'The step has been successfully deleted and all subtasks moved to Step 1.'
        });
        setSuccessDialogOpen(true);
      }
    } catch (error) {
      console.error('Error deleting:', error);
    } finally {
      setDeleteDialogOpen(false);
      setSubtaskToDelete(null);
      setStepToDelete(null);
      setDeleteType(null);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setSubtaskToDelete(null);
    setStepToDelete(null);
    setDeleteType(null);
  };

  const handleSaveSubtask = async (formData) => {
    try {
      if (editingSubtask) {
        await updateSubtask(editingSubtask.id, formData);
      } else {
        await saveSubtask(formData);
      }
      setIsDialogOpen(false);
      setEditingSubtask(null);
    } catch (error) {
      console.error('Error saving subtask:', error);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingSubtask(null);
  };

  const handleMoveSubtask = async (subtaskId: string, newStep: number, newStepName: string) => {
    await moveSubtask(subtaskId, newStep, newStepName);
  };

  const handleAddStep = async (stepName: string) => {
    await addStep(stepName);
  };

  const handleEditStep = async (step: number, newName: string) => {
    await editStep(step, newName);
  };

  if (!selectedProject) {
    return (
      <div className="h-full flex items-center justify-center">
        <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary">
          <CardContent className="p-8 text-center">
            <Users className="h-16 w-16 text-primary mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Project Selected</h3>
            <p className="text-gray-300">Please select a project from the Project Planning tab to manage tasks and subtasks.</p>
          </CardContent>
        </Card>
      </div>
    );
  }


  return (
    <div className="h-[calc(100vh-180px)] flex flex-col overflow-hidden">
      <ProjectInfoHeader selectedProject={selectedProject} />

      <div className="flex-1 flex gap-2 overflow-hidden min-h-0">
        {!isTasksListCollapsed && (
          <div className="w-80 flex-shrink-0 h-full">
            <TasksList
              uniqueTaskIds={resourceUniqueTaskIds}
              selectedTaskId={selectedTaskId}
              onTaskSelect={(taskId: string) => {
                console.log('🎯 TASK MANAGEMENT: Task selected, will auto-fetch assignments:', taskId);
                setSelectedTaskId(taskId);
                resourceSetSelectedTaskId(taskId);
              }}
              getAssignmentsForTask={getAssignmentsForTask}
              isLoading={resourceIsLoading}
              fullHeight
            />

          </div>
        )}

        {/* Collapse/Expand Button */}
        <div className="flex-shrink-0 flex items-start pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsTasksListCollapsed(!isTasksListCollapsed)}
            className="bg-secondary/50 border-primary text-primary hover:bg-secondary/70 hover:text-primary"
            title={isTasksListCollapsed ? "Show Project Tasks" : "Hide Project Tasks"}
          >
            {isTasksListCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>

        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <Card className="bg-secondary/30 border border-primary flex-1 flex flex-col min-h-0 h-full">
            <CardHeader className="pb-2 flex-shrink-0">
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <h2 className="text-white text-lg font-semibold">
                    Subtask Management
                    {selectedTaskId && (
                      <span className="text-sm text-gray-300 ml-2">
                        ({subtasks.length} subtasks)
                      </span>
                    )}
                  </h2>
                  <p className="text-primary text-[12px] mt-1">
                    {viewMode === 'steps' 
                      ? 'Drag subtasks between steps to organize your workflow'
                      : 'Click on the "To Do / In Progress / Done" buttons to update the subtask status'
                    }
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {selectedTaskId && (
                    <ToggleGroup type="single" value={viewMode} onValueChange={setViewMode}>
                      <ToggleGroupItem value="steps" aria-label="Steps workflow">
                        <Columns3Icon className="h-6 w-6 text-white" />
                      </ToggleGroupItem>
                      <ToggleGroupItem value="summary" aria-label="Summary view">
                        <Grid3X3 className="h-6 w-6 text-white" />
                      </ToggleGroupItem>
                      <ToggleGroupItem value="cards" aria-label="Card view">
                        <List className="h-6 w-6 text-white" />
                      </ToggleGroupItem>
                    </ToggleGroup>
                  )}
                  {selectedTaskId && (currentTaskAssignments.length > 0 || subtasks.length > 0) && (
                    <Button
                      onClick={handleAddSubtask}
                      disabled={isRestrictedUser}
                      className={`w-[155px] mx-[9px] ${
                        isRestrictedUser 
                          ? "bg-gray-600 text-gray-400 cursor-not-allowed hover:bg-gray-600"
                          : "bg-primary hover:bg-primary/90 text-black"
                      }`}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Subtask
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0 flex-1 flex flex-col min-h-0 pb-[10px]">
              {selectedTaskId && currentTaskAssignments.length === 0 && subtasks.length === 0 && (
                <Card className="mb-4 bg-yellow-900/20 border-yellow-500/30 flex-shrink-0">
                  <CardContent className="p-4">
                    <p className="text-yellow-300 text-sm">
                      ⚠️ No resources are assigned to this task. Please assign resources in the Resource Assignment tab before creating subtasks.
                    </p>
                  </CardContent>
                </Card>
              )}

              <div className="flex-1 min-h-0 overflow-hidden pb-[10px]">
                {viewMode === 'steps' ? (
                  <StepsContainer
                    subtasks={sortedSubtasks}
                    selectedTaskId={selectedTaskId}
                    isLoading={isLoading}
                    onEdit={handleEditSubtask}
                    onDelete={handleDeleteSubtask}
                    onStatusChange={updateSubtaskStatus}
                    onMoveSubtask={handleMoveSubtask}
                    onAddStep={handleAddStep}
                    onEditStep={handleEditStep}
                    onDeleteStep={handleDeleteStep}
                    availableSteps={availableSteps}
                    isRestrictedUser={isRestrictedUser}
                  />
                ) : viewMode === 'cards' ? (
                  <SubtasksList
                    subtasks={sortedSubtasks}
                    selectedTaskId={selectedTaskId}
                    isLoading={isLoading}
                    onEdit={handleEditSubtask}
                    onDelete={handleDeleteSubtask}
                    onStatusChange={updateSubtaskStatus}
                    isRestrictedUser={isRestrictedUser}
                  />
                ) : (
                  <SubtasksSummaryView
                    subtasks={sortedSubtasks}
                    selectedTaskId={selectedTaskId}
                    isLoading={isLoading}
                    onEdit={handleEditSubtask}
                    onDelete={handleDeleteSubtask}
                    onStatusChange={updateSubtaskStatus}
                    isRestrictedUser={isRestrictedUser}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <SubtaskForm
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveSubtask}
        editingSubtask={editingSubtask}
        resourceAssignments={
          role === 'Manager' && canManagerAddSubtasks && managerHierarchyEmails
            ? currentTaskAssignments.filter(a =>
                a.email ? managerHierarchyEmails.has(a.email.toLowerCase()) : false
              )
            : currentTaskAssignments
        }
        selectedTaskDetails={getSelectedTaskDetails()}
        existingSteps={getExistingSteps()}
        existingSubtasks={subtasks}
        projectId={selectedProject?.id}
      />


      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title={deleteType === 'subtask' ? 'Delete Subtask' : 'Delete Step'}
        description={
          deleteType === 'subtask' 
            ? 'Are you sure you want to delete this subtask? This action cannot be undone.'
            : 'Are you sure you want to delete this step? All subtasks in this step will be moved to Step 1.'
        }
        confirmText="Delete"
        cancelText="Cancel"
      />

      <SuccessDialog
        isOpen={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
        title={successMessage.title}
        description={successMessage.description}
      />
    </div>
  );
};

export default TaskManagementTab;
